import React, { useCallback, useRef } from 'react';
import { DraggableCore, DraggableEventHandler } from 'react-draggable';
import { twMerge } from 'tailwind-merge';

import Tooltip from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';
import { useMoveDrag } from '../hooks/useMoveDrag';
import { useSelectionKeyboard } from '../hooks/useSelectionKeyboard';
import { useTrimDrag } from '../hooks/useTrimDrag';
import {
  constrainSelectionTime,
  timeToAbsolutePixel,
} from '../utils/selectionCalculations';
import { VideoSelection } from '../utils/videoUtils';
import DurationLabel from './DurationLabel';
import TrimHandle from './TrimHandle';

interface SelectionIndicatorProps {
  selection: VideoSelection;
  videoDuration: number;
  stripWidth: number;
  stripHeight: number;
  onSelectionMove: (startTime: number) => void;
  onSelectionChange: (selection: VideoSelection) => void;
  className?: string;
  onSeek?: (time: number) => void;
  onPlayPause?: () => void;
  isPlaying?: boolean;
}

export default function SelectionIndicator({
  selection,
  videoDuration,
  stripWidth,
  stripHeight,
  onSelectionMove,
  onSelectionChange,
  className = '',
  onSeek,
  onPlayPause,
  isPlaying = false,
}: SelectionIndicatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMoveX = useRef<number>(0);

  const selectionWidth = timeToAbsolutePixel(
    selection.duration,
    videoDuration,
    stripWidth
  );

  const constrainedOnSelectionMove = useCallback(
    (startTime: number) => {
      const constrainedStartTime = constrainSelectionTime(
        startTime,
        selection.duration,
        videoDuration
      );
      onSelectionMove?.(constrainedStartTime);
    },
    [onSelectionMove, videoDuration, selection.duration]
  );

  const { handleKeyDown } = useSelectionKeyboard(
    videoDuration,
    constrainedOnSelectionMove,
    onSeek,
    onPlayPause,
    containerRef
  );

  const { handleMoveStart, handleMoveDrag, handleMoveStop } = useMoveDrag({
    selection,
    videoDuration,
    stripWidth,
    onSelectionMove: constrainedOnSelectionMove,
    onSeek,
    isPlaying,
    onPlayPause,
  });

  const {
    leftTooltipVisible,
    rightTooltipVisible,
    handleLeftTrimStart,
    handleLeftTrimDrag,
    handleLeftTrimStop,
    handleRightTrimStart,
    handleRightTrimDrag,
    handleRightTrimStop,
  } = useTrimDrag({
    selection,
    videoDuration,
    stripWidth,
    onSelectionChange,
    onSeek,
    isPlaying,
    onPlayPause,
  });

  const handleDragStart: DraggableEventHandler = useCallback(
    (_e, data) => {
      lastMoveX.current = data.x;
      handleMoveStart();
    },
    [handleMoveStart]
  );

  const handleDrag: DraggableEventHandler = useCallback(
    (_e, data) => {
      // DO NOT update lastMoveX here — useMoveDrag needs cumulative delta from drag start
      const cumulativeDeltaX = data.x - lastMoveX.current;
      handleMoveDrag(cumulativeDeltaX);
    },
    [handleMoveDrag]
  );

  const handleDragStop: DraggableEventHandler = useCallback(() => {
    lastMoveX.current = 0;
    handleMoveStop();
  }, [handleMoveStop]);

  const constrainedStartTime = constrainSelectionTime(
    selection.startTime,
    selection.duration,
    videoDuration
  );

  const absoluteContentPosition = timeToAbsolutePixel(
    constrainedStartTime,
    videoDuration,
    stripWidth
  );

  const selectionLeft = absoluteContentPosition;

  return (
    <div
      className={twMerge(
        'absolute pointer-events-none top-0 left-0 w-full',
        className
      )}
    >
      <div
        className="relative overflow-visible"
        ref={containerRef}
        style={{
          height: stripHeight,
          width: stripWidth,
        }}
      >
        <DraggableCore
          onStart={handleDragStart}
          onDrag={handleDrag}
          onStop={handleDragStop}
        >
          <div
            className={twMerge(
              'absolute pointer-events-auto selection-handle top-0',
              'z-10',
              'transition-colors',
              'rounded-lg',
              'outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-transparent'
            )}
            style={{
              left: `${selectionLeft}px`,
              width: `${selectionWidth}px`,
              height: `${stripHeight}px`,
              cursor: 'move',
            }}
            onKeyDown={(e) => handleKeyDown(e, selection)}
            tabIndex={0}
            role="slider"
            aria-label={`Video selection indicator. Duration: ${
              selection.duration
            } seconds. Start time: ${selection.startTime.toFixed(
              2
            )} seconds. Use arrow keys to move, Enter or Space to play/pause, Backspace to reset.`}
            aria-valuemin={0}
            aria-valuemax={videoDuration}
            aria-valuenow={selection.startTime}
            aria-valuetext={`Selection at ${selection.startTime.toFixed(
              2
            )} seconds, duration ${selection.duration.toFixed(2)} seconds`}
          >
            <div
              className="w-full h-full flex items-center relative rounded-xl border-yellow-400 border-t-2 border-b-2 border-l-8 border-r-8"
              style={{ cursor: 'move' }}
            >
              {/* Left grip line — centered in the 8px left border */}
              <div
                className="absolute -translate-y-1/2 bg-gray-900 rounded-full z-10 pointer-events-none"
                style={{ width: '2px', height: '24px', left: '-5px' }}
              />

              <div
                className="flex-1 h-full relative flex items-center justify-center"
                style={{ cursor: 'move' }}
              >
                <DurationLabel
                  duration={selection.duration}
                  selectionWidth={selectionWidth}
                />
              </div>

              {/* Right grip line — centered in the 8px right border */}
              <div
                className="absolute -translate-y-1/2 bg-gray-900 rounded-full z-10 pointer-events-none"
                style={{ width: '2px', height: '24px', right: '-5px' }}
              />
            </div>

            <Tooltip
              open={leftTooltipVisible}
              trigger={
                <TrimHandle
                  position="left"
                  onDragStart={handleLeftTrimStart}
                  onDrag={handleLeftTrimDrag}
                  onDragStop={handleLeftTrimStop}
                />
              }
              title={`${selection.startTime.toFixed(2)}s`}
              variant="light"
              hasArrow
              className="pointer-events-none"
              contentProps={{
                side: 'top',
                sideOffset: 8,
              }}
            />

            <Tooltip
              open={rightTooltipVisible}
              trigger={
                <TrimHandle
                  position="right"
                  onDragStart={handleRightTrimStart}
                  onDrag={handleRightTrimDrag}
                  onDragStop={handleRightTrimStop}
                />
              }
              title={`${selection.endTime.toFixed(2)}s`}
              variant="light"
              hasArrow
              className="pointer-events-none"
              contentProps={{
                side: 'top',
                sideOffset: 8,
              }}
            />
          </div>
        </DraggableCore>
      </div>
    </div>
  );
}
